import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X, Send, Lock, Brain, Sparkles, Mic, MicOff, Paperclip, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../translations';
import { useEducationAuth } from '../contexts/EducationAuthContext';
import { useAIChat } from '../contexts/AIChatContext';
import ListenButton from './ListenButton';
import SafeMarkdown from './SafeMarkdown';
import { a11yLabel } from '../utils/a11yLabels';

/**
 * ITER182 · Mobile-safe SHS-AI helper.
 * ─────────────────────────────────────
 * Auto-hides on routes where the floating widget would obscure a
 * critical primary CTA (camera capture, checkout, admin media deploy).
 * Everywhere else it renders as a small brain button that expands
 * on tap — never covers content by default.
 */
const HIDE_ON_ROUTES = [
  '/cpap-fit',       // camera capture + retake/skip CTAs bottom-right
  '/admin/media-deploy',  // rapid review keyboard workspace
];

const AIHelper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  // Lets other floating buttons step aside while the chat panel is open.
  useEffect(() => { document.body.classList.toggle('ai-chat-open', isOpen); }, [isOpen]);
  // ITER182 · Auto-close when route changes into a hide-list surface so
  // the floating button never covers a camera / rapid-review workspace.
  useEffect(() => {
    if (HIDE_ON_ROUTES.some((p) => location.pathname.startsWith(p))) {
      setIsOpen(false);
    }
  }, [location.pathname]);
  const hideOnRoute = HIDE_ON_ROUTES.some((p) => location.pathname.startsWith(p));

  // Phase 1 · MobileAssistantHub bridge — open the chat panel when the
  // unified mobile menu dispatches 'shs:open-ai'. Route-hidden surfaces
  // remain hidden.
  useEffect(() => {
    const openFromHub = () => {
      if (HIDE_ON_ROUTES.some((p) => location.pathname.startsWith(p))) return;
      setIsOpen(true);
    };
    window.addEventListener('shs:open-ai', openFromHub);
    return () => window.removeEventListener('shs:open-ai', openFromHub);
  }, [location.pathname]);

  const { language } = useLanguage();
  const t = (key) => getTranslation(language, key);
  const { user, isAuthenticated, hasAccess } = useEducationAuth();

  const { messages, isLoading, error, sendMessage, resetChat, addLocalTurn } = useAIChat();
  const [inputValue, setInputValue] = useState('');

  // ── Multimodal state ──────────────────────────────────────────────
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [attachmentError, setAttachmentError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const startRecording = async () => {
    setAttachmentError(null);
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setAttachmentError(language === 'es' ? 'Grabación no soportada en este navegador' : 'Recording not supported in this browser');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : '';
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
        await transcribeAndSend(blob);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      setAttachmentError(language === 'es' ? 'Permiso de micrófono denegado' : 'Microphone permission denied');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const transcribeAndSend = async (blob) => {
    setIsTranscribing(true);
    setAttachmentError(null);
    try {
      const fd = new FormData();
      fd.append('audio', blob, 'voice.webm');
      fd.append('language', language === 'es' ? 'es' : 'en');
      const res = await fetch(`${backendUrl}/api/assistant/transcribe`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('transcription_failed');
      const data = await res.json();
      const text = (data.text || '').trim();
      if (!text) {
        setAttachmentError(language === 'es' ? 'No se detectó voz' : 'No speech detected');
      } else {
        // Send transcribed text through the normal chat flow
        await sendMessage(text);
      }
    } catch {
      setAttachmentError(language === 'es' ? 'Error al transcribir el audio' : 'Audio transcription failed');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleAttachClick = () => {
    setAttachmentError(null);
    fileInputRef.current?.click();
  };

  const handleImageSelected = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ''; // reset so same file can be re-picked
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setAttachmentError(language === 'es' ? 'Solo se admiten imágenes por ahora' : 'Images only for now');
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setAttachmentError(language === 'es' ? 'Imagen demasiado grande (máx 20 MB)' : 'Image too large (max 20 MB)');
      return;
    }
    setIsAnalyzingImage(true);
    setAttachmentError(null);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('prompt', inputValue.trim() || '');
      fd.append('language', language === 'es' ? 'es' : 'en');
      const res = await fetch(`${backendUrl}/api/assistant/vision`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error('vision_failed');
      const data = await res.json();
      const reply = (data.reply || '').trim();
      // Inject the user's "attached image" turn + assistant's vision reply
      // directly into local chat history (no extra LLM roundtrip).
      const promptEcho = inputValue.trim()
        ? `📷 ${inputValue.trim()}`
        : (language === 'es' ? '📷 [Imagen adjuntada para análisis educativo]' : '📷 [Attached an image for educational analysis]');
      setInputValue('');
      addLocalTurn('user', promptEcho);
      addLocalTurn('assistant', reply);
    } catch {
      setAttachmentError(language === 'es' ? 'Error al analizar la imagen' : 'Image analysis failed');
    } finally {
      setIsAnalyzingImage(false);
    }
  };

  // Check if user has AI access (Master Mind or Professional tier)
  const hasAIAccess = hasAccess ? hasAccess('ai_assistant') : false;

  const handleSend = async () => {
    if (!inputValue.trim() || !hasAIAccess) return;
    const text = inputValue.trim();
    setInputValue('');
    await sendMessage(text);
  };

  const handleOpen = () => {
    setIsOpen(true);
    if (!hasAIAccess) return;
    if (messages.length === 0) {
      resetChat();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const renderMessages = () => {
    const base = messages.length === 0 && hasAIAccess
      ? [{ role: 'assistant', content: t('aiHelper.greeting') }]
      : messages;

    return base.map((message, index) => (
      <div
        key={index}
        className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
      >
        <div
          className={`max-w-[80%] rounded-lg p-3 ${
            message.role === 'user'
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-900 border border-gray-200'
          }`}
        >
          {message.role === 'assistant' ? (
            <SafeMarkdown className="text-sm">{message.content}</SafeMarkdown>
          ) : (
            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
          )}
          {message.role === 'assistant' && message.content && message.content.length >= 3 && (
            <ListenButton text={message.content} testid={`ai-listen-${index}`} />
          )}
        </div>
      </div>
    ));
  };

  return (
    <>
      {/* ITER182 · Hidden on hide-list routes to never obscure primary CTAs. */}
      {hideOnRoute ? null : (
        <>
      {/* Floating Button — hidden on mobile (unified into MobileAssistantHub); shown on ≥sm */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          aria-label="Open SHS-AI assistant"
          className="hidden sm:flex fixed bottom-6 right-6 z-40 bg-purple-600 hover:bg-purple-700 text-white rounded-full p-4 shadow-2xl transition-all duration-300 transform hover:scale-110 items-center gap-2"
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
          data-testid="ai-helper-btn"
        >
          <Brain className="h-6 w-6" aria-hidden="true" />
          <span className="font-semibold">SHS-AI</span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div
          className="fixed bottom-4 right-2 left-2 sm:left-auto sm:bottom-6 sm:right-6 z-50 sm:w-96 sm:max-w-[calc(100vw-3rem)] shadow-2xl"
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <Card className="border-2 border-purple-600">
            <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain className="h-5 w-5" />
                  <CardTitle className="text-lg">{language === 'es' ? 'Asistente SHS-AI' : 'SHS-AI Assistant'}</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClose}
                  className="text-white hover:bg-purple-800 h-8 w-8 p-0"
                 aria-label={a11yLabel("X")}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Messages Area / Locked State */}
              <div className="h-96 overflow-y-auto p-4 space-y-4 bg-gray-50">
                {!hasAIAccess ? (
                  <div className="flex flex-col items-center justify-center h-full text-center text-sm text-gray-700">
                    <div className="bg-purple-100 p-4 rounded-full mb-4">
                      <Lock className="h-8 w-8 text-purple-600" />
                    </div>
                    <p className="font-semibold text-gray-900 mb-2">{language === 'es' ? 'SHS-AI es solo para miembros' : 'SHS-AI is for members only'}</p>
                    <p className="text-xs text-gray-600 mb-4 max-w-xs">
                      {language === 'es'
                        ? 'Accede al asistente bilingüe de educación del sueño más avanzado con la membresía Master Mind ($10/mes) o Professional ($20/mes).'
                        : 'Get access to the most advanced bilingual sleep education assistant with Master Mind ($10/mo) or Professional ($20/mo) membership.'}
                    </p>
                    {!isAuthenticated ? (
                      <div className="flex flex-col gap-2 w-full">
                        <Button
                          size="sm"
                          className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                          onClick={() => {
                            handleClose();
                            navigate('/education/login');
                          }}
                        >
                          {language === 'es' ? 'Iniciar sesión' : 'Sign In'}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            handleClose();
                            navigate('/membership?plan=master_mind');
                          }}
                        >
                          {language === 'es' ? 'Ver planes de membresía' : 'View Membership Plans'}
                        </Button>
                      </div>
                    ) : (
                      <Button
                        size="sm"
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                        onClick={() => {
                          handleClose();
                          navigate('/membership?plan=master_mind');
                        }}
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        {language === 'es' ? 'Mejorar a Master Mind' : 'Upgrade to Master Mind'}
                      </Button>
                    )}
                    <p className="text-xs text-gray-500 mt-4">
                      {language === 'es' ? 'Solo educativo • Sin diagnóstico • Sin consejo médico' : 'Education-only • No diagnosis • No medical advice'}
                    </p>
                  </div>
                ) : (
                  <>
                    {renderMessages()}
                    {isLoading && (
                      <div className="flex justify-start">
                        <div className="max-w-[80%] rounded-lg p-3 bg-white text-gray-900 border border-gray-200">
                          <p className="text-sm">{language === 'es' ? 'Pensando...' : 'Thinking...'}</p>
                        </div>
                      </div>
                    )}
                    {error && (
                      <div className="text-xs text-red-600 mt-2">{error}</div>
                    )}
                  </>
                )}
              </div>

              {/* Input Area */}
              <div className="p-4 bg-white border-t border-gray-200">
                {hasAIAccess ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2 items-center">
                      {/* Attach image */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        onChange={handleImageSelected}
                        className="hidden"
                        data-testid="ai-chat-image-input"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleAttachClick}
                        disabled={isLoading || isRecording || isTranscribing || isAnalyzingImage}
                        className="h-9 w-9 p-0 shrink-0 text-purple-700 hover:bg-purple-50"
                        aria-label={language === 'es' ? 'Adjuntar imagen' : 'Attach image'}
                        title={language === 'es' ? 'Adjuntar imagen' : 'Attach image'}
                        data-testid="ai-chat-attach-btn"
                      >
                        {isAnalyzingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
                      </Button>

                      {/* Voice input */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={isRecording ? stopRecording : startRecording}
                        disabled={isLoading || isTranscribing || isAnalyzingImage}
                        className={`h-9 w-9 p-0 shrink-0 ${isRecording ? 'text-red-600 bg-red-50 hover:bg-red-100 animate-pulse' : 'text-purple-700 hover:bg-purple-50'}`}
                        aria-label={
                          isRecording
                            ? (language === 'es' ? 'Detener grabación' : 'Stop recording')
                            : (language === 'es' ? 'Hablar' : 'Speak')
                        }
                        title={
                          isRecording
                            ? (language === 'es' ? 'Detener' : 'Stop')
                            : (language === 'es' ? 'Hablar' : 'Speak')
                        }
                        data-testid="ai-chat-mic-btn"
                      >
                        {isTranscribing ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : isRecording ? (
                          <MicOff className="h-4 w-4" />
                        ) : (
                          <Mic className="h-4 w-4" />
                        )}
                      </Button>

                      <Input
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSend();
                          }
                        }}
                        placeholder={
                          isRecording
                            ? (language === 'es' ? 'Grabando…' : 'Recording…')
                            : isTranscribing
                              ? (language === 'es' ? 'Transcribiendo…' : 'Transcribing…')
                              : isAnalyzingImage
                                ? (language === 'es' ? 'Analizando imagen…' : 'Analyzing image…')
                                : t('aiHelper.placeholder')
                        }
                        className="flex-1"
                        disabled={isLoading || isRecording || isTranscribing || isAnalyzingImage}
                        data-testid="ai-chat-input"
                      />
                      <Button
                        onClick={handleSend}
                        disabled={isLoading || isRecording || isTranscribing || isAnalyzingImage || !inputValue.trim()}
                        className="bg-purple-600 hover:bg-purple-700 shrink-0"
                        data-testid="ai-chat-send"
                       aria-label={a11yLabel("Send")}>
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                    {attachmentError && (
                      <p className="text-xs text-red-600" data-testid="ai-chat-attachment-error">{attachmentError}</p>
                    )}
                    <p className="text-xs text-gray-500 text-center">
                      {user?.role === 'shs_admin' || user?.ai_questions_remaining === -1
                        ? (language === 'es' ? 'Preguntas ilimitadas' : 'Unlimited questions')
                        : `${user?.ai_questions_remaining || 0} ${language === 'es' ? 'preguntas restantes este mes' : 'questions remaining this month'}`}
                    </p>
                  </div>
                ) : (
                  <div className="text-center text-xs text-gray-500 py-2">
                    {language === 'es' ? 'Mejore a Master Mind o Professional para acceder a la IA' : 'Upgrade to Master Mind or Professional for AI access'}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
        </>
      )}
    </>
  );
};

export default AIHelper;
